import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Copy, Eye, EyeOff, RefreshCw, Send, Webhook, KeyRound, Code2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntegrationApi({ getAuthHeader }) {
  const [settings, setSettings] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [jsmEnabled, setJsmEnabled] = useState(false);
  const [jsmUrl, setJsmUrl] = useState('');
  const [jsmApiKey, setJsmApiKey] = useState('');
  const [jsmStatusUrl, setJsmStatusUrl] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingJsm, setTestingJsm] = useState(false);
  const [testingStatus, setTestingStatus] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/loans-tracker/settings`, getAuthHeader());
      setSettings(res.data);
      setWebhookUrl(res.data.webhook_url || '');
      setWebhookEnabled(!!res.data.webhook_enabled);
      setJsmEnabled(!!res.data.jsm_enabled);
      setJsmUrl(res.data.jsm_url || '');
      setJsmApiKey(res.data.jsm_api_key || '');
      setJsmStatusUrl(res.data.jsm_status_url || '');
    } catch (e) { toast.error('Failed to load integration settings'); }
  }, [getAuthHeader]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error('Copy failed'));
  };

  const persistSettings = async () => {
    await axios.put(`${API_URL}/admin/loans-tracker/settings`,
      { webhook_url: webhookUrl, webhook_enabled: webhookEnabled, jsm_enabled: jsmEnabled, jsm_url: jsmUrl, jsm_api_key: jsmApiKey, jsm_status_url: jsmStatusUrl },
      getAuthHeader());
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await persistSettings();
      toast.success('Integration settings saved');
      fetchSettings();
    } catch (e) { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const testJsm = async () => {
    setTestingJsm(true);
    try {
      await persistSettings();
      const res = await axios.post(`${API_URL}/admin/loans-tracker/test-jsm`, {}, getAuthHeader());
      if (res.data.success) toast.success(`Test loan pushed to JSM (HTTP ${res.data.status_code}) — check the JSM tracker for "Test 2K2Day-Link" and delete it`);
      else toast.error(`JSM push failed: ${res.data.error || res.data.response || `HTTP ${res.data.status_code}`}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'JSM test failed'); }
    setTestingJsm(false);
  };

  const testJsmStatus = async () => {
    setTestingStatus(true);
    try {
      await persistSettings();
      const res = await axios.post(`${API_URL}/admin/loans-tracker/test-jsm-status`, {}, getAuthHeader());
      if (res.data.success) toast.success(`Status sync working (HTTP ${res.data.status_code}) — approvals/declines will now update JSM`);
      else toast.error(`Status sync failed: ${res.data.error || res.data.response || `HTTP ${res.data.status_code}`}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Status sync test failed'); }
    setTestingStatus(false);
  };

  const startBackfill = async () => {
    if (!window.confirm('Push ALL 2k2day applications that were never sent to JSM? Each will appear as a new loan in the JSM tracker. Applications already pushed are skipped, so no duplicates.')) return;
    setBackfilling(true);
    try {
      await persistSettings();
      const res = await axios.post(`${API_URL}/admin/loans-tracker/backfill-jsm`, {}, getAuthHeader());
      toast.success(res.data.message);
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API_URL}/admin/loans-tracker/settings`, getAuthHeader());
          setSettings(s.data);
          if (!s.data.backfill_status?.running) {
            clearInterval(poll);
            setBackfilling(false);
            const b = s.data.backfill_status || {};
            toast.success(`Backfill done: ${b.pushed || 0} pushed, ${b.failed || 0} failed`);
          }
        } catch { clearInterval(poll); setBackfilling(false); }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed to start');
      setBackfilling(false);
    }
  };

  const regenerateKey = async () => {
    if (!window.confirm('Regenerate API key? The old key will stop working immediately and must be updated in the JSM tracker.')) return;
    try {
      const res = await axios.post(`${API_URL}/admin/loans-tracker/regenerate-key`, {}, getAuthHeader());
      toast.success('New API key generated');
      setSettings(s => ({ ...s, api_key: res.data.api_key }));
      setShowKey(true);
    } catch (e) { toast.error('Failed to regenerate key'); }
  };

  const testWebhook = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/admin/loans-tracker/test-webhook`, {}, getAuthHeader());
      if (res.data.success) toast.success(`Test webhook delivered (HTTP ${res.data.status_code})`);
      else toast.error(`Test webhook failed: ${res.data.error || `HTTP ${res.data.status_code}`}`);
      fetchSettings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Test webhook failed'); }
    setTesting(false);
  };

  const maskedKey = settings?.api_key ? `${settings.api_key.slice(0, 6)}${'•'.repeat(20)}` : '';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" />API Key</CardTitle>
          <CardDescription>External systems (e.g. JSM Loans Tracker) authenticate with this key via the X-API-Key header</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="bg-slate-100 px-3 py-2 rounded-md text-sm font-mono flex-1 min-w-[200px] break-all" data-testid="integration-api-key">
              {showKey ? settings?.api_key : maskedKey}
            </code>
            <Button variant="outline" size="sm" onClick={() => setShowKey(v => !v)} data-testid="toggle-api-key-btn">
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={() => copyText(settings?.api_key || '', 'API key')} data-testid="copy-api-key-btn">
              <Copy className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={regenerateKey} data-testid="regenerate-api-key-btn">
              <RefreshCw className="w-4 h-4 mr-1" />Regenerate
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4 text-blue-600" />Push to JSM Loan Tracker</CardTitle>
          <CardDescription>Automatically create a loan in the JSM tracker (jsmt.co.za) every time a new 2k2day application is submitted — uses JSM's own Public API</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>JSM Public API URL</Label>
            <Input value={jsmUrl} onChange={e => setJsmUrl(e.target.value)}
              placeholder="https://<jsm-backend>/api/loans-tracker/public/apply" data-testid="jsm-url-input" />
            <p className="text-[11px] text-slate-400">Find this in the JSM tracker under "Public API / Embed"</p>
          </div>
          <div className="space-y-1.5">
            <Label>JSM API Key</Label>
            <Input value={jsmApiKey} onChange={e => setJsmApiKey(e.target.value)} type="password"
              placeholder="lt_..." data-testid="jsm-key-input" />
          </div>
          <div className="space-y-1.5">
            <Label>JSM Status Update URL <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input value={jsmStatusUrl} onChange={e => setJsmStatusUrl(e.target.value)}
              placeholder="https://jsmt.co.za/api/loans-tracker/public/status" data-testid="jsm-status-url-input" />
            <p className="text-[11px] text-slate-400">When set, 2k2day approvals/declines are synced to JSM. Requires the JSM app to have a status endpoint — leave blank until it exists.</p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={jsmEnabled} onCheckedChange={setJsmEnabled} data-testid="jsm-enabled-switch" />
            <Label className="cursor-pointer" onClick={() => setJsmEnabled(v => !v)}>Auto-push new applications to JSM</Label>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={saveSettings} disabled={saving} data-testid="save-jsm-btn">Save</Button>
            <Button size="sm" variant="outline" onClick={testJsm} disabled={testingJsm || !jsmUrl} data-testid="test-jsm-btn">
              <Send className="w-4 h-4 mr-1" />{testingJsm ? 'Pushing…' : 'Push Test Loan to JSM'}
            </Button>
            <Button size="sm" variant="outline" onClick={testJsmStatus} disabled={testingStatus || !jsmStatusUrl} data-testid="test-jsm-status-btn">
              <Send className="w-4 h-4 mr-1" />{testingStatus ? 'Testing…' : 'Test Status Sync'}
            </Button>
            <Button size="sm" variant="outline" onClick={startBackfill} disabled={backfilling || !jsmEnabled} data-testid="backfill-jsm-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${backfilling ? 'animate-spin' : ''}`} />{backfilling ? 'Backfilling…' : 'Backfill All Existing Loans'}
            </Button>
          </div>
          {settings?.backfill_status && (
            <p className="text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1.5" data-testid="backfill-status">
              Last backfill: {settings.backfill_status.running ? 'running… ' : 'done — '}
              {settings.backfill_status.pushed ?? 0} pushed, {settings.backfill_status.failed ?? 0} failed
              {settings.backfill_status.total != null ? ` of ${settings.backfill_status.total}` : ''}
              {settings.backfill_status.finished_at ? ` (${new Date(settings.backfill_status.finished_at).toLocaleString()})` : ''}
            </p>
          )}
          <p className="text-[11px] text-amber-600 bg-amber-50 rounded px-2 py-1.5">Failed pushes retry automatically every 10 minutes (up to 7 days), so no application is missed. Only new applications are pushed once enabled — use Backfill for older ones.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Webhook className="w-4 h-4" />Webhook Push Notifications</CardTitle>
          <CardDescription>2k2day will POST to this URL when a new application is submitted or its status changes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Webhook URL</Label>
            <Input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
              placeholder="https://jsmt.co.za/api/webhooks/2k2day-loans" data-testid="webhook-url-input" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={webhookEnabled} onCheckedChange={setWebhookEnabled} data-testid="webhook-enabled-switch" />
            <Label className="cursor-pointer" onClick={() => setWebhookEnabled(v => !v)}>Webhook enabled</Label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveSettings} disabled={saving} data-testid="save-webhook-btn">Save</Button>
            <Button size="sm" variant="outline" onClick={testWebhook} disabled={testing || !webhookUrl} data-testid="test-webhook-btn">
              <Send className="w-4 h-4 mr-1" />{testing ? 'Sending…' : 'Send Test Webhook'}
            </Button>
          </div>
          {settings?.recent_webhooks?.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Recent webhook deliveries</Label>
              <div className="space-y-1" data-testid="webhook-log-list">
                {settings.recent_webhooks.map(w => (
                  <div key={w.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5">
                    <Badge className={w.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {w.success ? `OK ${w.status_code || ''}` : 'Failed'}
                    </Badge>
                    <span className="font-medium">{w.event}</span>
                    {w.reference && <span className="text-slate-500">#{w.reference}</span>}
                    <span className="text-slate-400 ml-auto">{w.created_at ? new Date(w.created_at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Code2 className="w-4 h-4" />API Endpoints</CardTitle>
          <CardDescription>All requests require header: X-API-Key: &lt;your key&gt;</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="space-y-1">
            <p className="font-semibold">List / filter applications</p>
            <div className="flex items-center gap-2">
              <code className="bg-slate-100 px-2 py-1.5 rounded text-xs break-all flex-1">GET {API_URL}/loans-tracker/applications</code>
              <Button variant="ghost" size="sm" onClick={() => copyText(`${API_URL}/loans-tracker/applications`, 'URL')}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            <p className="text-xs text-slate-500">
              Query params: <code>status</code> (submitted / pending / under_review / approved / declined / cancelled), <code>date_from</code>, <code>date_to</code> (YYYY-MM-DD), <code>since</code> (ISO timestamp — poll for new applications), <code>id_number</code>, <code>reference</code>, <code>limit</code>, <code>offset</code>
            </p>
          </div>
          <div className="space-y-1">
            <p className="font-semibold">Get one application by reference</p>
            <div className="flex items-center gap-2">
              <code className="bg-slate-100 px-2 py-1.5 rounded text-xs break-all flex-1">GET {API_URL}/loans-tracker/applications/&#123;reference&#125;</code>
              <Button variant="ghost" size="sm" onClick={() => copyText(`${API_URL}/loans-tracker/applications/{reference}`, 'URL')}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            <p className="text-xs text-slate-500">Returns application summary + full status history</p>
          </div>
          <div className="space-y-1">
            <p className="font-semibold">Webhook payload (sent to your URL)</p>
            <pre className="bg-slate-900 text-slate-100 rounded-md p-3 text-xs overflow-x-auto">{`{
  "event": "application.created" | "application.status_changed" | "test",
  "timestamp": "2026-06-01T10:00:00+00:00",
  "application": {
    "id": "...", "reference": "12345678",
    "first_name": "...", "last_name": "...", "full_name": "...",
    "id_number": "...", "cell_number": "...", "email": "...",
    "loan_amount": 2000, "total_repayment": 2600, "repayment_period": 30,
    "status": "submitted", "created_at": "...", "updated_at": "..."
  }
}`}</pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
